/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import {useTranslation} from "react-i18next";
import clsx from "clsx";
import {PackageItemCompetition} from "types/entities/PackageItem";
import {useCompetitionSelection} from "utils/hooks/useCompetitionSelection";
import {getDependsOnName, translatedText} from "pages/Front/Booking/Competitions/competitionUtils";
import CompetitionActionButton from "pages/Front/Booking/Competitions/components/CompetitionActionButton";
import CompetitionPrice from "pages/Front/Booking/Competitions/components/CompetitionPrice";
import CompetitionActionCountButton from "pages/Front/Booking/Competitions/components/CompetitionActionCountButton";

interface CompetitionOptionItemProps {
    competition: PackageItemCompetition;
    groupItems: PackageItemCompetition[];
}

const CompetitionOptionItem = (props: CompetitionOptionItemProps) => {
    const {t} = useTranslation();
    const {isFreeSelect, isFree, isSelected, isDisabled, hasUnmetDependency, hasDepends, isFamilyCategory, toggleCompetition, count, setCount, maxCount} = useCompetitionSelection(
        props.competition
    );

    const articleId: number = props.competition.article.id;
    const isTrickyBoxAdditionalSessions: boolean = articleId === 14110;

    const {minimum_persons_count, maximum_persons_count} = props.competition;
    const hasPersonsRange = minimum_persons_count > 0 || maximum_persons_count > 0;
    const dependsOnName = hasUnmetDependency ? getDependsOnName(props.competition, props.groupItems) : "";

    return (
        <div
            className={clsx(
                "CompetitionOptionItem flex flex-col md:flex-row md:items-center gap-2 md:gap-4 w-full border-t border-[#D9D9D9] px-4 py-3 md:py-2 transition-colors duration-200",
                isSelected ? "bg-[#FEFBF2]" : "",
                isDisabled && "text-black/60"
            )}
        >
            <div className={"flex flex-col grow gap-y-1"}>
                <div className={"flex flex-col xs:flex-row sm:items-center gap-x-3 gap-y-1 grow"}>
                    <h4 className={clsx("text-base font-bold leading-tight", isDisabled ? "text-black/60" : "text-black")}>
                        {translatedText(props.competition.name)}
                    </h4>

                    {!hasDepends && hasPersonsRange && (
                        <span className={"rounded bg-[#E5E5E5] px-2 py-1 text-[10px] font-bold whitespace-nowrap self-baseline"}>
                            {isFamilyCategory && props.competition.competition?.needs_credit
                                ? t("creditsminimum", {count: props.competition.competition?.needs_credit_amount})
                                : maximum_persons_count === 500
                                ? t("personsminimum", {count: minimum_persons_count})
                                : t("personsmin_max", {min: minimum_persons_count, max: maximum_persons_count})}
                        </span>
                    )}
                    {dependsOnName && <span className={"text-xs"}>{t("Only with {{name}}", {name: dependsOnName})}</span>}
                </div>
            </div>

            <div className={clsx("flex flex-col xs:flex-row items-center gap-3 md:gap-5 shrink-0 w-full md:w-auto", isFree ? "justify-end" : "justify-between md:justify-end")}>
                {!isFree && (
                    <div className={"w-auto flex justify-start md:justify-end"}>
                        <CompetitionPrice className={clsx(isDisabled && "!text-black/60")} competition={props.competition} />
                    </div>
                )}
                <div className={clsx("flex", isTrickyBoxAdditionalSessions ? "w-auto justify-center xs:justify-end" : "w-full xs:w-auto justify-end")}>
                    {isTrickyBoxAdditionalSessions
                        ? <CompetitionActionCountButton
                            className={"w-full xs:w-auto"}
                            disabled={isDisabled}
                            isFreeSelect={isFreeSelect}
                            isSelected={isSelected}
                            count={count}
                            toggleCompetition={toggleCompetition}
                            setCount={setCount}
                            maxCount={maxCount}
                        />
                        : <CompetitionActionButton className={"w-full xs:w-auto"} disabled={isDisabled} isFreeSelect={isFreeSelect} isSelected={isSelected} onClick={toggleCompetition} />
                    }
                </div>
            </div>
        </div>
    );
};

export default CompetitionOptionItem;
